import React, {
  useState,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  Checkbox,
  Chip,
  ListItemText,
  TextField as MuiTextField,
} from '@mui/material';
import {
  DatePicker,
  TimePicker,
} from '@mui/lab';
import format from 'string-template';
import firebase from 'firebase/app';

import DialogRoundedModal, { ModalBody } from '../../../../../components/DialogRoundedModal';
import InputLabel from '../../../../../components/v2/InputLabel';
import TextField from '../../../../../components/v2/TextField';
import ToggleButtonGroup from '../../../../components/ToggleButtonGroup';
import { SaveButton } from '../../../../../components/Button/ActionButtons';
import useStorage from '../../../../../hooks/useStorage';
import UserContext from '../../../../../context/UserContext';
import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import AnnouncementContext from '../../../../context/AnnouncementContext';
import useToast from '../../../../hooks/useToast';
import { useCoachConfig } from '../../../../hooks/useCoachConfig';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import AttachmentsContainer from '../../../../components/AttachmentsContainer';
import AudioAttachmentsContainer from
'../../../Chat/components/MessagingBroadcastModal/components/AudioAttachmentsContainer';
import Announcement, {
  SCHEDULED_STATUS,
  hasCommunityGroup,
} from '../../../../Model/Announcement';
import { GENERIC_COMMUNITY, CommunityType } from '../../../../Model/Community';
import { getCommunityLabel } from '../../../../utils/announcement';
import { combineDateAndTimeWithUTCOffset, isToday } from '../../../../../utils/date';
import { getUTCOffset, roundToNext5Minutes } from '../../../../../utils/time';
import { MAX_FILE_UPLOAD_SIZE_MB } from '../../../../../utils/files';
import EmojiInput from '../../../Chat/components/EmojiInput';
import { SelectOption } from '../../styles';

import {
  processAttachments,
  onFilesSelected,
  DELIVERY_MODE,
  ONE_ON_ONE_ID,
  DEFAULT_COMMUNITY_ID,
  buildPostFieldsForCommunity,
  resolveCommunityIdForAnnouncement,
  buildCreateCommunityFields,
  buildNewSiblingData,
} from './utils';
import {
  RecipientActionsContainer,
  StyledTypography,
  StyledAnnouncementIcon,
  SelectOptionsContainer,
  DeliveryContainer,
  SectionLabel,
  DateSelectorContainer,
  DateSelectorField,
  StyledAlert,
  CommunityAutoComplete,
  RecipientsNote,
} from './styles';
import texts from './texts.json';

const MAX_MESSAGE_LENGTH = 2000;
const EMPTY_VALUE = '';

const AnnouncementModal = ({
  isOpen,
  onDidDismiss,
  announcement,
}) => {
  const { userId: loggedInUserId } = useContext(LoggedInUserContext);
  const { userId: coachId, userDoc } = useContext(UserContext);
  const { coachDoc } = useContext(ExternalCoachContext);
  const { coachConfig } = useCoachConfig(coachId);
  const isPodEnabled = !!coachConfig?.isPodEnabled;

  const { avatarUrl: coachAvatarUrl, name: coachName } = userDoc || {};
  const timezoneOffset = getUTCOffset(coachDoc?.timeZone || moment.tz.guess());
  const isEdit = !!announcement;

  const [announcementMessage, setAnnouncementMessage] = useState(EMPTY_VALUE);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCommunityIds, setSelectedCommunityIds] = useState([DEFAULT_COMMUNITY_ID]);
  // The post's whole group, so an edit knows every community it went to. A post created before
  // multi-select is simply a group of one.
  const [groupSiblings, setGroupSiblings] = useState([]);
  const [isLoadingGroup, setIsLoadingGroup] = useState(false);
  // The group couldn't be read, so the selection on screen is incomplete and must not be edited.
  const [hasGroupLoadFailed, setHasGroupLoadFailed] = useState(false);
  // Set once the coach edits the recipients, so the preselect effect stops overwriting them.
  const isSelectionDirtyRef = useRef(false);
  const { communities } = useContext(AnnouncementContext);
  const [fileAttachments, setFileAttachments] = useState([]);
  const [audioAttachment, setAudioAttachment] = useState(null);
  const [deliveryMode, setDeliveryMode] = useState(DELIVERY_MODE.POST_NOW);
  const [scheduledDate, setScheduledDate] = useState(() => moment().utcOffset(timezoneOffset));
  const [scheduledTime, setScheduledTime] = useState(() => (
    roundToNext5Minutes(moment().utcOffset(timezoneOffset))
  ));

  // Coach timezone can resolve after mount (ExternalCoachContext). Initial state may use moment.tz.guess();
  // re-sync create flow pickers when the real offset arrives.
  useEffect(() => {
    if (!isEdit) {
      setScheduledDate(moment().utcOffset(timezoneOffset));
      setScheduledTime(roundToNext5Minutes(moment().utcOffset(timezoneOffset)));
    }
  }, [isEdit, timezoneOffset]);

  const isPublishedAnnouncement = isEdit
    && (!announcement?.scheduledStatus || announcement?.scheduledStatus === SCHEDULED_STATUS.COMPLETED);

  const {
    uploadAttachments,
    deleteAttachments,
  } = useStorage();

  const { showToast } = useToast();

  // Populate the modal with the announcement data in case of an edit.
  useEffect(() => {
    if (isEdit) {
      const {
        message,
        attachments,
        scheduledStatus,
        publishAt,
      } = announcement;

      setAnnouncementMessage(message);
      if (attachments?.length) {
        setFileAttachments(attachments.map((attachment) => ({
          name: attachment.title,
          type: attachment.mimeType,
          size: attachment.fileSize,
          url: attachment.assetPath,
        })));
      }
      if (scheduledStatus === SCHEDULED_STATUS.PENDING && publishAt) {
        const publishMoment = moment(publishAt.toDate()).utcOffset(timezoneOffset);
        setDeliveryMode(DELIVERY_MODE.SCHEDULE);
        setScheduledDate(publishMoment);
        setScheduledTime(publishMoment.clone());
      }
    }
  }, [isEdit, announcement, timezoneOffset]);

  // When editing, load every copy of the post so the recipients field knows the whole audience.
  // Deliberately independent of `communities`: that array is rebuilt by an autorun on any community
  // doc change, and reading the group again each time would be wasted. Mapping copies to community
  // ids is `siblingCommunities`' job, and it does re-run when the list resolves.
  useEffect(() => {
    if (!isEdit) {
      return undefined;
    }
    let cancelled = false;

    const loadGroup = async () => {
      if (!hasCommunityGroup(announcement)) {
        setGroupSiblings([announcement]);
        return;
      }
      setIsLoadingGroup(true);
      setHasGroupLoadFailed(false);
      try {
        const siblings = await Announcement.getActiveGroupSiblings(announcement);
        if (!cancelled) {
          setGroupSiblings(siblings);
        }
      } catch {
        if (!cancelled) {
          // Fall back to editing just this copy, but lock the recipients: the other communities
          // are unknown here, so re-adding one would create a second copy alongside the one that
          // failed to load.
          setGroupSiblings([announcement]);
          setHasGroupLoadFailed(true);
          showToast(texts.toast.loadGroupFailed, { error: true });
        }
      } finally {
        if (!cancelled) {
          setIsLoadingGroup(false);
        }
      }
    };

    loadGroup();
    return () => {
      cancelled = true;
    };
  }, [isEdit, announcement, showToast]);

  const handleFileSelection = useCallback((event) => {
    const files = onFilesSelected(event);
    if (files.length) {
      setFileAttachments((prevFiles) => [...prevFiles, ...files]);
    } else {
      showToast(format(texts.toast.fileSizeError, {
        maxFileSize: MAX_FILE_UPLOAD_SIZE_MB,
      }), { error: true });
    }
  }, [showToast]);

  const removeFile = useCallback((index) => {
    const resultAttachments = [...fileAttachments];
    resultAttachments.splice(index, 1);
    setFileAttachments(resultAttachments);
  }, [fileAttachments]);

  const isMessageValid = announcementMessage.trim().length <= MAX_MESSAGE_LENGTH;

  const minTime = useMemo(() => (
    isToday(moment(scheduledDate).utcOffset(timezoneOffset)) ? moment().utcOffset(timezoneOffset) : null
  ), [scheduledDate, timezoneOffset]);

  // Each existing copy of the post paired with the community it targets. Preselection, the pinned
  // lock, the change warning and the save-time diff all read from this one mapping, so they cannot
  // disagree about which community a copy belongs to.
  const siblingCommunities = useMemo(() => {
    const siblings = groupSiblings.length ? groupSiblings : [announcement].filter(Boolean);
    return siblings.map((sibling) => ({
      sibling,
      communityId: resolveCommunityIdForAnnouncement(sibling, communities),
    }));
  }, [groupSiblings, announcement, communities]);

  const currentCommunityIds = useMemo(() => {
    const ids = siblingCommunities.map(({ communityId }) => communityId);
    // Open in the stored order, so the chips the coach saw on the card are the chips they edit. A
    // community missing from the stored list - added by an older build - sorts last rather than
    // being dropped.
    const storedOrder = announcement?.groupCommunityIds ?? [];
    const orderOf = (id) => {
      const index = storedOrder.indexOf(id);
      return index === -1 ? storedOrder.length : index;
    };
    return ids.slice().sort((a, b) => orderOf(a) - orderOf(b));
  }, [siblingCommunities, announcement]);

  // Preselect the communities the post already went to, re-running as the community list resolves
  // so the legacy pod/tag fallbacks get a second chance. Stops once the coach has touched the
  // field, so a late update cannot discard their edits.
  useEffect(() => {
    if (!isEdit || isSelectionDirtyRef.current) {
      return;
    }
    setSelectedCommunityIds(currentCommunityIds);
  }, [isEdit, currentCommunityIds]);

  // Community id -> the name the copy stored, so an option can still be labelled when the id no
  // longer resolves to one of the coach's communities.
  const siblingCommunityNames = useMemo(
    () => new Map(siblingCommunities.map(({ sibling, communityId }) => [communityId, sibling.communityName])),
    [siblingCommunities],
  );

  const handleSaveAnnouncement = useCallback(async () => {
    if (!announcementMessage.trim()) {
      showToast(texts.toast.empty, { error: true });
      return;
    }

    if (!selectedCommunityIds.length) {
      showToast(texts.toast.noCommunities, { error: true });
      return;
    }

    // The group never loaded, so `siblingCommunities` holds only this copy. Saving would write the
    // message to it alone while rewriting its groupCommunityIds down to its own community, leaving
    // the other copies stale and the group's metadata wrong - with nothing on screen to say so.
    if (hasGroupLoadFailed) {
      showToast(texts.toast.groupLoadFailedSave, { error: true });
      return;
    }

    const isScheduling = deliveryMode === DELIVERY_MODE.SCHEDULE && !isPublishedAnnouncement;

    // Build a timezone-aware schedule timestamp only when scheduling is active.
    const scheduledDateTime = isScheduling && scheduledDate && scheduledTime
      ? combineDateAndTimeWithUTCOffset(scheduledDate, scheduledTime, timezoneOffset)
      : null;
    const nowInCoachTimezone = moment().utcOffset(timezoneOffset);

    // Single validation gate for scheduling: both date/time must exist and be in the future.
    if (isScheduling && (!scheduledDateTime || !scheduledDateTime.isAfter(nowInCoachTimezone))) {
      showToast(texts.toast.invalidSchedule, { error: true });
      return;
    }

    try {
      setIsProcessing(true);

      const { attachments, error } = await processAttachments({
        fileAttachments,
        audioAttachment,
        coachId,
        loggedInUserId,
        uploadAttachments,
        deleteAttachments,
      });

      if (error) {
        showToast(texts.toast.uploadFailed, { error: true });
        // Restore the existing attachments and delete the uploaded ones in case of an upload failure
        setFileAttachments(attachments);
        return;
      }

      const message = announcementMessage.trim();
      const publishAt = scheduledDateTime ? scheduledDateTime.toDate() : null;
      const selectionFor = (communityId) => buildPostFieldsForCommunity(communityId, communities, isPodEnabled);

      if (isEdit) {
        const basePostId = announcement.basePostId || siblingCommunities[0].sibling.id;

        const keptTargets = siblingCommunities.filter(
          ({ communityId }) => selectedCommunityIds.includes(communityId),
        );
        const removedSiblings = siblingCommunities
          .filter(({ communityId }) => !selectedCommunityIds.includes(communityId))
          .map(({ sibling }) => sibling);
        const existingCommunityIds = new Set(currentCommunityIds);
        const addedCommunityIds = selectedCommunityIds.filter((id) => !existingCommunityIds.has(id));
        // Every selected id is either kept or added, so this is the selection itself - stored in the
        // order the coach is looking at, which is what orders the card's chips and the rail's rows.
        const groupCommunityIds = selectedCommunityIds;

        // A sibling's community is immutable - retargeting is remove + create - so the shared update
        // deliberately leaves communityId/communityName/tagId/tagName/podId untouched. That also
        // keeps a post's comment thread with the audience that wrote it.
        const sharedFields = {
          message,
          attachments,
          basePostId,
          groupCommunityIds,
          lastUpdatedAt: new Date(),
          lastUpdatedBy: loggedInUserId,
        };

        // One timestamp for the whole group, so siblings stay adjacent in every feed ordering. This
        // holds while the group is written here; a *scheduled* group gets one Cloud Task per sibling
        // and functions-announcement-processor sets createdAt per doc at dispatch, so the published
        // copies land milliseconds apart. Group by basePostId, never by an exact createdAt match.
        const publishedNowAt = new Date();
        // Saving a still-pending post without a schedule publishes it now, which rewrites every
        // sibling's createdAt - a community added in the same save has to follow suit.
        const isPublishingNow = !(isScheduling && publishAt) && !isPublishedAnnouncement;
        const buildScheduleFields = (sibling) => {
          if (isScheduling && publishAt) {
            return {
              publishAt,
              // Scheduled posts stay hidden from the published feed until processed; omit redundant writes.
              ...(sibling.scheduledStatus !== SCHEDULED_STATUS.PENDING
                ? { scheduledStatus: SCHEDULED_STATUS.PENDING } : {}),
              ...(!sibling.isRemoved ? { isRemoved: true } : {}),
            };
          }
          if (isPublishedAnnouncement) {
            return { isRemoved: false };
          }
          return {
            isRemoved: false,
            // Post now: drop schedule-only fields so the docs match immediate posts, keeping the
            // original creation time for audit.
            publishAt: firebase.firestore.FieldValue.delete(),
            scheduledStatus: firebase.firestore.FieldValue.delete(),
            originalCreatedAt: sibling.createdAt,
            createdAt: publishedNowAt,
          };
        };

        await Announcement.commitGroupChanges({
          updates: keptTargets.map(({ sibling }) => ({
            announcement: sibling,
            fields: { ...sharedFields, ...buildScheduleFields(sibling) },
          })),
          creates: addedCommunityIds.map((communityId) => buildNewSiblingData({
            selection: selectionFor(communityId),
            base: siblingCommunities[0].sibling,
            message,
            attachments,
            basePostId,
            groupCommunityIds,
            loggedInUserId,
            schedule: { isScheduling, publishAt, scheduledStatus: SCHEDULED_STATUS.PENDING },
            ...(isPublishingNow ? { createdAt: publishedNowAt } : {}),
          })),
          removals: removedSiblings,
          canceledBy: loggedInUserId,
        });
      } else {
        // One shared createdAt across the group, for the same adjacency reason as above.
        const createdAt = new Date();
        await Announcement.addGroupDocs(selectedCommunityIds.map((communityId) => ({
          message,
          coach: coachId,
          createdAt,
          createdBy: loggedInUserId,
          likes: 0,
          attachments,
          isRemoved: isScheduling,
          isByCoach: true,
          postAvatarUrl: coachAvatarUrl,
          postUserName: coachName,
          ...(isScheduling && publishAt && {
            publishAt,
            scheduledStatus: SCHEDULED_STATUS.PENDING,
          }),
          ...buildCreateCommunityFields(selectionFor(communityId)),
        })));
      }

      showToast(texts.toast.success, { success: true });
      onDidDismiss();
    } catch (error) {
      showToast(format(texts.toast.failed, { error: error.message }), { error: true });
    } finally {
      setIsProcessing(false);
    }
  }, [
    announcementMessage,
    deliveryMode,
    scheduledDate,
    scheduledTime,
    showToast,
    onDidDismiss,
    coachId,
    loggedInUserId,
    selectedCommunityIds,
    siblingCommunities,
    currentCommunityIds,
    hasGroupLoadFailed,
    communities,
    isPodEnabled,
    fileAttachments,
    audioAttachment,
    uploadAttachments,
    deleteAttachments,
    coachAvatarUrl,
    coachName,
    announcement,
    isEdit,
    timezoneOffset,
    isPublishedAnnouncement,
  ]);

  const cursorPosRef = useRef(null);

  const setEmoji = useCallback((e) => {
    setAnnouncementMessage((prevText) => {
      const pos = cursorPosRef.current ?? (prevText?.length ?? 0);
      return `${prevText?.slice(0, pos) ?? ''}${e.emoji}${prevText?.slice(pos) ?? ''}`;
    });
    cursorPosRef.current = (cursorPosRef.current ?? 0) + e.emoji.length;
  }, []);

  // Grouped recipient options: Generic → Pods → Custom → Tag, in that order so each section can be
  // rendered under one subheader.
  const communityOptions = useMemo(() => {
    const toOption = (group) => (item) => ({ id: item.id, label: item.name, group });
    const podOption = toOption(texts.podCommunities);
    const customOption = toOption(texts.customCommunities);
    const tagOption = toOption(texts.tagCommunities);
    const generic = GENERIC_COMMUNITY.map(toOption(texts.genericCommunities));
    const { pods, custom, tag } = communities.reduce(
      (acc, community) => {
        if (community.type === CommunityType.POD) acc.pods.push(podOption(community));
        else if (community.type === CommunityType.CUSTOM) acc.custom.push(customOption(community));
        else if (community.type === CommunityType.TAG) acc.tag.push(tagOption(community));
        return acc;
      },
      { pods: [], custom: [], tag: [] },
    );
    return [...generic, ...pods, ...custom, ...tag];
  }, [communities]);

  // A sibling can target a community the options list doesn't cover - an archived one, since
  // Community.getByCoach filters those out. Without a synthetic option its chip would silently
  // disappear and saving would delete that sibling.
  const resolvedCommunityOptions = useMemo(() => {
    const knownIds = new Set(communityOptions.map((option) => option.id));
    const syntheticOptions = selectedCommunityIds
      .filter((id) => !knownIds.has(id))
      .map((id) => ({
        id,
        label: getCommunityLabel(id, communities, siblingCommunityNames.get(id)),
        group: texts.otherCommunities,
      }));
    return [...communityOptions, ...syntheticOptions];
  }, [communityOptions, selectedCommunityIds, communities, siblingCommunityNames]);

  const selectedCommunityOptions = useMemo(
    () => selectedCommunityIds
      .map((id) => resolvedCommunityOptions.find((option) => option.id === id))
      .filter(Boolean),
    [selectedCommunityIds, resolvedCommunityOptions],
  );

  // Only one post can be pinned per community, enforced at pin time. Removing a pinned sibling here
  // would delete the pinned post outright, so its community stays put until the post is unpinned.
  const lockedCommunityIds = useMemo(
    () => siblingCommunities
      .filter(({ sibling }) => sibling.isPinned)
      .map(({ communityId }) => communityId),
    [siblingCommunities],
  );

  // Generic 1:1 Coaching posts to every pod (podId: ''), so a pod community picked alongside it is
  // a strict subset of it. The overlap is allowed - a coach may well want both - but its members
  // get two copies and, until the server-side election lands, two notifications. Name it rather
  // than silently deciding the audience for them.
  const overlappingPodNames = useMemo(() => {
    if (!selectedCommunityIds.includes(ONE_ON_ONE_ID)) {
      return [];
    }
    return communities
      .filter((community) => community.type === CommunityType.POD && selectedCommunityIds.includes(community.id))
      .map((community) => community.name);
  }, [selectedCommunityIds, communities]);

  const handleCommunitiesChange = useCallback((_, options) => {
    isSelectionDirtyRef.current = true;
    const nextIds = options.map((option) => option.id);
    // Disabled options can't be toggled, but re-add any locked community as a backstop.
    const strippedLockedIds = lockedCommunityIds.filter((id) => !nextIds.includes(id));
    setSelectedCommunityIds([...nextIds, ...strippedLockedIds]);
  }, [lockedCommunityIds]);

  // Warn before saving a change of audience, since it creates and deletes copies of the post.
  const hasCommunityChanges = useMemo(() => {
    if (!isEdit) {
      return false;
    }
    const currentIds = new Set(currentCommunityIds);
    return currentIds.size !== selectedCommunityIds.length
      || selectedCommunityIds.some((id) => !currentIds.has(id));
  }, [isEdit, currentCommunityIds, selectedCommunityIds]);

  // An Autocomplete rather than a Select: a coach with a dozen pod communities types to find one
  // instead of scrolling. `groupBy` builds the sections, so the options list stays flat.
  const renderSelectField = useMemo(() => (
    <CommunityAutoComplete
      multiple
      disableCloseOnSelect
      disableClearable
      options={resolvedCommunityOptions}
      value={selectedCommunityOptions}
      onChange={handleCommunitiesChange}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option.label || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      // A pinned community can't be removed here: unpinning is what frees its pin slot, and
      // dropping the sibling would delete the pinned post.
      getOptionDisabled={(option) => lockedCommunityIds.includes(option.id)}
      disabled={hasGroupLoadFailed}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.id}>
          <Checkbox checked={selected} />
          <ListItemText primary={option.label} />
        </li>
      )}
      // A locked community keeps its chip but loses the delete handle, so the list and the chips
      // agree on what can be removed.
      renderTags={(value, getTagProps) => value.map((option, index) => {
        const { onDelete, ...tagProps } = getTagProps({ index });
        return (
          <Chip
            {...tagProps}
            key={option.id}
            size="small"
            label={option.label}
            {...(!lockedCommunityIds.includes(option.id) && { onDelete })}
          />
        );
      })}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          variant="outlined"
          placeholder={selectedCommunityOptions.length ? '' : texts.selectCommunities}
        />
      )}
    />
  ), [
    resolvedCommunityOptions,
    selectedCommunityOptions,
    handleCommunitiesChange,
    lockedCommunityIds,
    hasGroupLoadFailed,
  ]);

  let saveButtonLabel = texts.button.create;
  if (isEdit) {
    saveButtonLabel = texts.button.update;
  }

  return (
    <>
      <DialogRoundedModal
        open={isOpen}
        onClose={onDidDismiss}
        title={isEdit ? texts.title.edit : texts.title.new}
        IconComponent={<StyledAnnouncementIcon />}
        maxWidth="md"
        fullWidth
        actionButtons={(
          <SaveButton
            variant="contained"
            onClick={handleSaveAnnouncement}
            disabled={
              !announcementMessage.trim()
              || !isMessageValid
              || !selectedCommunityIds.length
              || hasGroupLoadFailed
            }
          >
            {saveButtonLabel}
          </SaveButton>
        )}
      >
        <ModalBody>
          <SelectOptionsContainer>
            <SelectOption>
              <InputLabel>{texts.labels.tag}</InputLabel>
              {renderSelectField}
              {hasGroupLoadFailed && (
                <RecipientsNote>{texts.recipientsLockedOnLoadError}</RecipientsNote>
              )}
              {!hasGroupLoadFailed && !!lockedCommunityIds.length && (
                <RecipientsNote>{texts.pinnedRecipientsLocked}</RecipientsNote>
              )}
              {!hasGroupLoadFailed && !!overlappingPodNames.length && (
                <RecipientsNote>
                  {format(texts.overlappingPodRecipients, { pods: overlappingPodNames.join(', ') })}
                </RecipientsNote>
              )}
              {hasCommunityChanges && (
                <StyledAlert severity="warning">{texts.communityChangeWarning}</StyledAlert>
              )}
            </SelectOption>
          </SelectOptionsContainer>
          <InputLabel>{texts.labels.message}</InputLabel>
          <TextField
            value={announcementMessage}
            rows={4}
            multiline
            onChange={(e) => setAnnouncementMessage(e.target.value)}
            onBlur={(e) => { cursorPosRef.current = e.target.selectionStart; }}
            error={!isMessageValid}
            helperText={!isMessageValid ? format(texts.maxLength, { maxLength: MAX_MESSAGE_LENGTH }) : ''}
            $withoutMargin
          />
          <RecipientActionsContainer>
            <StyledTypography>
              {format(texts.count, { count: announcementMessage.length, maxLength: MAX_MESSAGE_LENGTH })}
            </StyledTypography>
            <EmojiInput onEmojiClick={setEmoji} />
          </RecipientActionsContainer>
          <AttachmentsContainer
            fileAttachments={fileAttachments}
            onFilesSelected={handleFileSelection}
            onFileRemoved={removeFile}
            isUploading={false}
          />
          <AudioAttachmentsContainer
            onAudioRecorded={(audioFile) => setAudioAttachment(audioFile)}
            onRemove={() => setAudioAttachment(null)}
            isUploading={false}
          />
          {!isPublishedAnnouncement && (
            <DeliveryContainer>
              <SectionLabel>{texts.labels.delivery}</SectionLabel>
              <ToggleButtonGroup
                value={deliveryMode}
                exclusive
                onChange={setDeliveryMode}
                size="small"
                options={[
                  { value: DELIVERY_MODE.POST_NOW, label: texts.delivery.postNow },
                  { value: DELIVERY_MODE.SCHEDULE, label: texts.delivery.scheduleForLater },
                ]}
              />
              {deliveryMode === DELIVERY_MODE.SCHEDULE && (
                <>
                  <StyledAlert severity="warning">{texts.scheduleWarningMessage}</StyledAlert>
                  <DateSelectorContainer>
                    <DatePicker
                      label={texts.labels.scheduleDate}
                      value={scheduledDate}
                      onChange={(newValue) => setScheduledDate(newValue)}
                      minDate={moment().utcOffset(timezoneOffset)}
                      renderInput={(params) => (
                        <DateSelectorField {...params} size="small" />
                      )}
                    />
                    <TimePicker
                      label={texts.labels.scheduleTime}
                      value={scheduledTime}
                      onChange={(newValue) => setScheduledTime(newValue)}
                      minutesStep={5}
                      minTime={minTime}
                      renderInput={(params) => (
                        <DateSelectorField {...params} size="small" />
                      )}
                    />
                  </DateSelectorContainer>
                </>
              )}
            </DeliveryContainer>
          )}
        </ModalBody>
      </DialogRoundedModal>
      <LoadingOverlay isLoading={isProcessing || isLoadingGroup} />
    </>
  );
};

AnnouncementModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDidDismiss: PropTypes.func.isRequired,
  announcement: PropTypes.instanceOf(Announcement),
};

AnnouncementModal.defaultProps = {
  announcement: null,
};

export default AnnouncementModal;
